import React, { useEffect, useState } from "react";
import { useTable, useSortBy, Column } from "react-table";
import { UserInfoForList, UserRow, UserShortInfo } from "../../types";
import { TableBurgerIcon } from "../../icons/tableBurger";
import Paginator from "../paginator";
import { CellSortIcon } from "../../icons/sort";
import ShortManInfo from "../ui/shortItem";
import { backend } from "../../services/backend";
import { defPageSize } from "../../constants/events";
import { useDispatch, useSelector } from "react-redux";
import { actions, RootState } from "../../state/reducer";
import { SettingsIcon } from "../../icons";
import { LockIcon } from "../../icons/lock";
import { closeModal, openModal } from "../../state/modalSlice";
import UpdateUserSettingsModal from "../../modals/users/updateSettings";
import ConfirmationModal from "../../modals/yesNoWindow";
import { toast } from "sonner";
import { formatDateTimeStandard, generateRandomString } from "../../utils";
import { UnLockIcon } from "../../icons/unlock";
import TransferActivityModal from "../../modals/users/transferUserActivity";
import { UsersPageIcon } from "../../icons/menu/users";

interface UserTableProps {
  columns: Column<UserInfoForList>[];
  data: UserInfoForList[];
}

const tableGridTemplate = "1.5fr 2fr 3fr 3fr 3fr 1fr";

const UsersTable: React.FC<UserTableProps> = ({ columns, data }) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<UserTableProps>({ columns, data }, useSortBy);

  return (
    <div
      className="listTable"
      {...getTableProps()}
      style={{ display: "grid", gap: "10px" }}
    >
      {/* Заголовок таблицы */}
      <div
        className="tableHeading"
        style={{
          display: "grid",
          gridTemplateColumns: tableGridTemplate,
        }}
      >
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column) => (
            <div
              {...column.getHeaderProps(column.getSortByToggleProps())}
              style={{ cursor: column.canSort ? "pointer" : "default" }}
            >
              {column.render("Header")}
              {column.Header !== "" && typeof column.Header === "string" ? (
                <CellSortIcon
                  width={8}
                  height={5}
                  classNames={
                    column.isSortedDesc
                      ? ["sortedDesc", "grayFilled"]
                      : ["grayFilled"]
                  }
                />
              ) : null}
            </div>
          ))
        )}
      </div>

      {/* Тело таблицы */}
      <div {...getTableBodyProps()} style={{ display: "grid", gap: "10px" }}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <div
              {...row.getRowProps()}
              style={{
                display: "grid",
                gridTemplateColumns: tableGridTemplate,
                padding: "10px 0",
                borderBottom: "1px solid #ccc",
              }}
            >
              {row.cells.map((cell: any) => (
                <div {...cell.getCellProps()}>{cell.render("Cell")}</div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const UserListTable = (props: {
  search: string;
  isAdminPage?: boolean;
  onUserClickCallback?: (user: UserShortInfo) => void;
}) => {
  const State = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const [displayUsers, setDisplayUsers] = useState<UserInfoForList[]>([]);
  const [usersCount, setUsersCount] = useState<number>(0);

  const filters = State.userListFilters;
  const trigger = State.selectedItem;
  const rights = State.rights;

  console.log("Filters:", filters);

  useEffect(() => {
    dispatch(
      actions.userRequestParams({ ...filters, searchQuery: props.search })
    );
  }, [props.search]);

  useEffect(() => {
    console.log("User filters trigger");
  }, [State.userListFilters]);

  useEffect(() => {
    backend
      .getAllUsers(filters)
      .then((res) => {
        setDisplayUsers(res.users);
      })
      .catch((e) => {
        console.log(e);
      });
    backend.getAllUsersCount(filters).then((res) => {
      setUsersCount(res.count);
    });
  }, [filters, trigger]);

  const handlePageSelect = (data: { page: number; perPage: number }) => {
    const offset = data.page * data.perPage;
    dispatch(
      actions.userRequestParams({
        ...filters,
        offset: Number(offset),
        limit: data.perPage,
      })
    );
  };

  const onClickCallBack: (user: UserShortInfo) => void =
    props.onUserClickCallback ||
    ((user: UserShortInfo) => {
      document.location.href = `/profile/${user.id}`;
    });

  const columns: Column<UserInfoForList>[] = [
    /* 
        photo?: string;
    name?: string;
    phone?: string;
    */
    {
      Header: "ФИО",
      accessor: "name",
      canSort: true,
      Cell: ({ row }: any) => (
        <a
          className="aBtn"
          onClick={onClickCallBack.bind(this, row.original.short)}
        >
          <ShortManInfo
            photo={row.original.short.photo}
            name={row.original.short.name}
            phone={row.original.short.phone}
          />
        </a>
      ),
    },
    {
      Header: "Роль",
      accessor: "role",
      Cell: ({ row }: any) => (
        <div className="userRoleTableText">{row.original.role}</div>
      ),
    },
    {
      Header: "Группы",
      accessor: "group",
      Cell: ({ row }: any) => (
        <div className="userRoleTableText">
          {row.original.groups.join(", ")}
        </div>
      ),
    },
    {
      Header: "Дата регистрации",
      accessor: "date_employed",
      canSort: true,
      Cell: ({ row }: any) =>
        row.original.date_employed
          ? formatDateTimeStandard(row.original.date_employed)
          : "",
    },
    {
      Header: "Дата увольнения",
      accessor: "date_unemployed",
      canSort: true,
      Cell: ({ row }: any) =>
        row.original.date_unemployed
          ? formatDateTimeStandard(row.original.date_unemployed)
          : "",
    },
    {
      Header: "",
      accessor: "actions",
      Cell: ({ row }: any) => {
        const handleOpenUpdate = () => {
          dispatch(
            openModal({
              component: UpdateUserSettingsModal,
              context: {
                user_id: row.original.short?.id,
                onSave: () => dispatch(closeModal()),
                closeModal: () => dispatch(closeModal()), // Закрыть модальное окно через Redux
              },
            })
          );
        };

        const handleBlockUser = (unblock?: boolean) => {
          const user = row.original.short;

          // Проверка наличия пользователя
          if (!user || !user.id || !user.name) {
            toast("Ошибка: данные пользователя не найдены");
            return;
          }

          const action = unblock ? "разблокировать" : "заблокировать";
          const actionPast = unblock ? "разблокирован" : "заблокирован";
          const backendMethod = unblock ? "unBlockUser" : "blockUser";

          // Открытие модального окна подтверждения
          dispatch(
            openModal({
              component: ConfirmationModal,
              context: {
                user_id: user.id,
                question: `${action} пользователя ${user.name}?`,
                onConfirm: () => {
                  backend[backendMethod](user.id)
                    .then(() => {
                      toast(`Пользователь ${actionPast}`);
                      dispatch(actions.selectItem(generateRandomString(3)));
                      dispatch(closeModal()); // Закрыть модальное окно после успешного выполнения
                    })
                    .catch((error) => {
                      console.error("Ошибка при выполнении запроса:", error);
                      toast(`Ошибка: не удалось ${action} пользователя`);
                      dispatch(closeModal()); // Закрыть модальное окно в случае ошибки
                    });
                },
                onDecline: () => {
                  toast("Действие отменено");
                  dispatch(closeModal()); // Закрыть модальное окно при отмене
                },
                closeModal: () => dispatch(closeModal()), // Закрыть модальное окно через Redux
              },
            })
          );
        };

        const handleOpenTransfer = () => {
          dispatch(
            openModal({
              component: TransferActivityModal,
              context: {
                user_id: row.original.short?.id,
                onSave: () => dispatch(closeModal()),
                closeModal: () => dispatch(closeModal()), // Закрыть модальное окно через Redux
              },
            })
          );
        };

        return (
          <div className="flex gap6">
            {!props.onUserClickCallback &&
              row.original.date_unemployed &&
              rights.rang < 3 && (
                <>
                  <div
                    className="aBtn iconBtn gray"
                    onClick={handleOpenTransfer.bind(this)}
                  >
                    <UsersPageIcon width={24} height={24} />
                    <div className="hint">Передать задачи и клиентов</div>
                  </div>
                  <div
                    className="aBtn iconBtn gray"
                    onClick={handleBlockUser.bind(this, true)}
                  >
                    <UnLockIcon width={24} height={24} />
                    <div className="hint">Разблокировать</div>
                  </div>
                </>
              )}
            {!props.onUserClickCallback &&
              !row.original.date_unemployed &&
              rights.rang &&
              row.original.rang_id > rights.rang && (
                <>
                  <div className="aBtn iconBtn gray" onClick={handleOpenUpdate}>
                    <SettingsIcon width={24} height={24} />
                    <div className="hint">Настройки ролей пользователя</div>
                  </div>
                  <div
                    className="aBtn iconBtn gray"
                    onClick={handleOpenTransfer.bind(this)}
                  >
                    <UsersPageIcon width={24} height={24} />
                    <div className="hint">Передать задачи и клиентов</div>
                  </div>
                  <div
                    className="aBtn iconBtn red"
                    onClick={handleBlockUser.bind(this, false)}
                  >
                    <LockIcon width={24} height={24} />
                    <div className="hint">Заблокировать</div>
                  </div>
                </>
              )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="tableContainer">
      <UsersTable data={displayUsers} columns={columns} />
      <Paginator
        count={usersCount}
        perPage={defPageSize}
        onDataChange={handlePageSelect}
      />
    </div>
  );
};

export default UserListTable;
